import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  useToast,
  useColorModeValue,
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Image,
  Center,
  Flex,
  useBreakpointValue
} from '@chakra-ui/react';
import { FiArrowLeft, FiUpload, FiX, FiVideo, FiImage } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { GENRES } from '../constants/genres';
import { CONTENT_RATINGS } from '../constants/contentRatings';
import { getContentForEdit, updateContent } from '../services/contentUpdateService';
import { deleteContent } from '../services/contentDeletionService';
import { uploadFileDirect } from '../services/uploadService';

interface ShortData {
  id: string;
  title: string;
  description: string;
  category: string;
  visibility: string;
  rating: string;
  language: string;
  genres: string[];
  cast: string;
  director: string;
  writer: string;
  assets?: {
    [key: string]: string | undefined;
    poster?: string;
  };
  poster?: string;
  thumbnail?: string;
}

const EditShorts: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [short, setShort] = useState<ShortData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'short',
    visibility: 'public',
    rating: 'G',
    language: 'English',
    genres: [] as string[],
    cast: '',
    director: '',
    writer: '',
    posterFile: null as File | null,
    posterUrl: '',
    videoFile: null as File | null,
    videoUrl: ''
  });

  const bgColor = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const loadShortData = async () => {
    if (!id || !user) return;
    
    try {
      setLoading(true);
      const shortData = await getContentForEdit(id, user.uid);
      
      if (shortData) {
        setShort(shortData as unknown as ShortData);
        
        // Find video URL from assets
        let videoUrl = '';
        if (shortData.assets) {
          for (const [key, value] of Object.entries(shortData.assets)) {
            if (typeof value === 'string' && 
                (key.toLowerCase().includes('.mp4') || 
                 key.toLowerCase().includes('.mov') || 
                 key.toLowerCase().includes('.avi') ||
                 key.toLowerCase().includes('.webm'))) {
              videoUrl = value;
              break;
            }
          }
        }

        setFormData({
          title: shortData.title || '',
          description: shortData.description || '',
          category: shortData.category || 'short',
          visibility: shortData.visibility || 'public',
          rating: shortData.contentRating || 'G',
          language: shortData.language || 'English',
          genres: shortData.genres || [],
          cast: shortData.cast || '',
          director: shortData.director || '',
          writer: shortData.writer || '',
          posterFile: null,
          posterUrl: shortData.poster || shortData.thumbnail || '',
          videoFile: null,
          videoUrl: videoUrl
        });
      }
    } catch (error) {
      console.error('Error loading short data:', error);
      toast({ title: 'Error', description: 'Failed to load short data.', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShortData();
  }, [id, user]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenreToggle = (genre: string) => {
    const currentGenres = formData.genres;
    const isSelected = currentGenres.includes(genre);
    
    if (isSelected) {
      setFormData(prev => ({
        ...prev,
        genres: currentGenres.filter(g => g !== genre)
      }));
    } else if (currentGenres.length < 3) {
      setFormData(prev => ({
        ...prev,
        genres: [...currentGenres, genre]
      }));
    }
  };

  const handlePosterSelect = (file: File) => {
    setFormData(prev => ({
      ...prev,
      posterFile: file,
      posterUrl: URL.createObjectURL(file)
    }));
  };

  const handlePosterRemove = () => {
    if (formData.posterUrl && formData.posterFile) {
      URL.revokeObjectURL(formData.posterUrl);
    }
    setFormData(prev => ({
      ...prev,
      posterFile: null,
      posterUrl: short?.poster || short?.thumbnail || ''
    }));
  };

  const handleVideoSelect = (file: File) => {
    setFormData(prev => ({
      ...prev,
      videoFile: file,
      videoUrl: URL.createObjectURL(file)
    }));
  };

  const handleVideoRemove = () => {
    if (formData.videoUrl && formData.videoFile) {
      URL.revokeObjectURL(formData.videoUrl);
    }
    setFormData(prev => ({
      ...prev,
      videoFile: null,
      videoUrl: ''
    }));
  };

  const handleSave = async () => {
    if (!user || !id) return;

    if (!formData.title.trim()) {
      toast({ title: 'Title required', description: 'Please enter a title.', status: 'warning' });
      return;
    }

    if (formData.genres.length === 0) {
      toast({ title: 'Genres required', description: 'Please select at least one genre.', status: 'warning' });
      return;
    }

    setSaving(true);
    setUploadProgress(0);

    try {
      let newPosterUrl = formData.posterUrl;
      if (formData.posterFile) {
        newPosterUrl = await uploadFileDirect(`shorts/${user.uid}/${id}/poster`, formData.posterFile);
        setUploadProgress(30);
      }

      let newVideoUrl = formData.videoUrl;
      if (formData.videoFile) {
        newVideoUrl = await uploadFileDirect(`shorts/${user.uid}/${id}/video`, formData.videoFile);
        setUploadProgress(60);
      }

      const updateData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        visibility: formData.visibility,
        rating: formData.rating,
        language: formData.language,
        genres: formData.genres,
        cast: formData.cast,
        director: formData.director,
        writer: formData.writer,
        posterFile: formData.posterFile || undefined,
        videoFile: formData.videoFile || undefined,
      };

      await updateContent(id, user.uid, updateData);
      setUploadProgress(90);

      // Update assets in Firestore
      const { doc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('../lib/firebase');
      
      const assets: any = {
        poster: newPosterUrl
      };
      
      if (newVideoUrl) {
        // Store video with filename as key
        const videoFileName = formData.videoFile?.name || 'video.mp4';
        assets[videoFileName] = newVideoUrl;
      }

      await updateDoc(doc(db, 'content', id), {
        'assets.poster': newPosterUrl,
        'assets': assets,
        'poster': newPosterUrl,
        'thumbnail': newPosterUrl
      });

      setUploadProgress(100);
      toast({ title: 'Short updated successfully!', status: 'success' });
      navigate(`/short/${id}`);
    } catch (error) {
      console.error('Error saving short:', error);
      toast({ title: 'Error saving short', description: 'Please try again.', status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteShort = async () => {
    if (!user || !id) return;

    if (window.confirm('Are you sure you want to delete this short? This action cannot be undone.')) {
      try {
        setSaving(true);
        await deleteContent(id, user.uid);
        
        toast({ title: 'Short deleted', description: 'Your short has been deleted.', status: 'success' });
        navigate('/shorts');
      } catch (error) {
        console.error('Error deleting short:', error);
        toast({ title: 'Error deleting short', description: 'Please try again.', status: 'error' });
      } finally {
        setSaving(false);
      }
    }
  };

  if (loading) {
    return (
      <Center h="100vh">
        <VStack gap={4}>
          <Text>Loading short data...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor} p={{ base: 4, md: 8 }}>
      <VStack maxW="800px" mx="auto" gap={8} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <HStack gap={4}>
            <IconButton
              aria-label="Go back"
              icon={<IconWrapper icon={FiArrowLeft} size={20} />}
              onClick={() => navigate(-1)}
              variant="ghost"
            />
            <Text fontSize="2xl" fontWeight="bold" color={textColor}>
              Edit Short
            </Text>
          </HStack>
          
          <HStack gap={2}>
            <Button
              variant="outline"
              colorScheme="red"
              onClick={handleDeleteShort}
              isLoading={saving}
              loadingText="Deleting..."
              isDisabled={saving}
            >
              Delete
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleSave}
              isLoading={saving}
              loadingText="Saving..."
              isDisabled={saving}
            >
              Save Changes
            </Button>
          </HStack>
        </HStack>

        {/* Progress Bar */}
        {saving && (
          <Box w="100%" bg="gray.200" borderRadius="full" h="4px">
            <Box
              bg="purple.500"
              h="100%"
              borderRadius="full"
              transition="width 0.3s"
              w={`${uploadProgress}%`}
            />
          </Box>
        )}

        {/* Form */}
        <VStack gap={6} align="stretch">
          {/* Title */}
          <FormControl>
            <FormLabel color={labelColor} fontWeight="bold">
              Short Title *
            </FormLabel>
            <Input
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Enter short title"
              bg={inputBg}
              borderColor={borderColor}
            />
          </FormControl>

          {/* Description */}
          <FormControl>
            <FormLabel color={labelColor} fontWeight="bold">
              Description *
            </FormLabel>
            <Textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Enter short description"
              bg={inputBg}
              borderColor={borderColor}
              rows={4}
            />
          </FormControl>

          {/* Genres */}
          <FormControl>
            <FormLabel color={labelColor} fontWeight="bold">
              Genres * (Select up to 3)
            </FormLabel>
            <Flex wrap="wrap" gap={2}>
              {GENRES.map(genre => (
                <Button
                  key={genre}
                  size="sm"
                  variant={formData.genres.includes(genre) ? 'solid' : 'outline'}
                  colorScheme="purple"
                  onClick={() => handleGenreToggle(genre)}
                  isDisabled={!formData.genres.includes(genre) && formData.genres.length >= 3}
                >
                  {genre}
                </Button>
              ))}
            </Flex>
          </FormControl>

          {/* Cast, Director, Writer */}
          <HStack gap={4} align="start" wrap="wrap">
            <FormControl flex="1" minW="200px">
              <FormLabel color={labelColor} fontWeight="bold">
                Cast
              </FormLabel>
              <Input
                value={formData.cast}
                onChange={(e) => handleInputChange('cast', e.target.value)}
                placeholder="Actor names (comma separated)"
                bg={inputBg}
                borderColor={borderColor}
              />
            </FormControl>
            <FormControl flex="1" minW="200px">
              <FormLabel color={labelColor} fontWeight="bold">
                Director
              </FormLabel>
              <Input
                value={formData.director}
                onChange={(e) => handleInputChange('director', e.target.value)}
                placeholder="Director name(s)"
                bg={inputBg}
                borderColor={borderColor}
              />
            </FormControl>
            <FormControl flex="1" minW="200px">
              <FormLabel color={labelColor} fontWeight="bold">
                Writer
              </FormLabel>
              <Input
                value={formData.writer}
                onChange={(e) => handleInputChange('writer', e.target.value)}
                placeholder="Writer name(s)"
                bg={inputBg}
                borderColor={borderColor}
              />
            </FormControl>
          </HStack>

          {/* Content Rating */}
          <FormControl>
            <FormLabel color={labelColor} fontWeight="bold">
              Content Rating
            </FormLabel>
            <RadioGroup
              value={formData.rating}
              onChange={(value) => handleInputChange('rating', value)}
            >
              <HStack gap={4} wrap="wrap">
                {CONTENT_RATINGS.map(rating => (
                  <Radio key={rating.value} value={rating.value} colorScheme="purple">
                    {rating.label}
                  </Radio>
                ))}
              </HStack>
            </RadioGroup>
          </FormControl>

          {/* Language */}
          <FormControl>
            <FormLabel color={labelColor} fontWeight="bold">
              Language
            </FormLabel>
            <Input
              value={formData.language}
              onChange={(e) => handleInputChange('language', e.target.value)}
              placeholder="English"
              bg={inputBg}
              borderColor={borderColor}
            />
          </FormControl>

          {/* Thumbnail Upload */}
          <FormControl>
            <FormLabel color={labelColor} fontWeight="bold">
              Thumbnail
            </FormLabel>
            <Box
              border="2px dashed"
              borderColor={formData.posterFile ? 'purple.300' : borderColor}
              borderRadius="lg"
              p={8}
              textAlign="center"
              bg={formData.posterFile ? 'purple.50' : inputBg}
              _dark={{ bg: formData.posterFile ? 'purple.900' : 'gray.700' }}
              cursor="pointer"
              _hover={{ borderColor: 'purple.400' }}
              transition="all 0.2s"
              onClick={() => document.getElementById('thumbnail-upload')?.click()}
              minH="200px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {formData.posterFile ? (
                <VStack gap={3}>
                  <Box position="relative">
                    <Image
                      src={formData.posterUrl}
                      alt="Thumbnail preview"
                      maxH="150px"
                      maxW="200px"
                      objectFit="cover"
                      borderRadius="md"
                    />
                    <IconButton
                      aria-label="Remove thumbnail"
                      icon={<IconWrapper icon={FiX} size={16} />}
                      size="sm"
                      colorScheme="red"
                      position="absolute"
                      top="-8px"
                      right="-8px"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePosterRemove();
                      }}
                    />
                  </Box>
                  <Text fontSize="sm" color={textColor}>
                    Click to change thumbnail
                  </Text>
                </VStack>
              ) : (
                <VStack gap={3}>
                  <IconWrapper icon={FiImage} size={48} color="gray.400" />
                  <Text color={textColor} fontWeight="medium">
                    Click to upload thumbnail
                  </Text>
                  <Text fontSize="sm" color={labelColor}>
                    PNG, JPG up to 5MB
                  </Text>
                </VStack>
              )}
            </Box>
            <Input
              id="thumbnail-upload"
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handlePosterSelect(file);
                }
              }}
              display="none"
            />
          </FormControl>

          {/* Video Upload */}
          <FormControl>
            <FormLabel color={labelColor} fontWeight="bold">
              Short Video *
            </FormLabel>
            <Box
              border="2px dashed"
              borderColor={formData.videoFile ? 'purple.300' : borderColor}
              borderRadius="lg"
              p={8}
              textAlign="center"
              bg={formData.videoFile ? 'purple.50' : inputBg}
              _dark={{ bg: formData.videoFile ? 'purple.900' : 'gray.700' }}
              cursor="pointer"
              _hover={{ borderColor: 'purple.400' }}
              transition="all 0.2s"
              onClick={() => document.getElementById('video-upload')?.click()}
              minH="200px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {formData.videoFile ? (
                <VStack gap={3}>
                  <Box position="relative">
                    <IconWrapper icon={FiVideo} size={48} color="purple.500" />
                    <Text fontSize="sm" color={textColor} fontWeight="medium">
                      {formData.videoFile.name}
                    </Text>
                    <IconButton
                      aria-label="Remove video"
                      icon={<IconWrapper icon={FiX} size={16} />}
                      size="sm"
                      colorScheme="red"
                      position="absolute"
                      top="-8px"
                      right="-8px"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVideoRemove();
                      }}
                    />
                  </Box>
                  <Text fontSize="sm" color={textColor}>
                    Click to change video
                  </Text>
                </VStack>
              ) : (
                <VStack gap={3}>
                  <IconWrapper icon={FiVideo} size={48} color="gray.400" />
                  <Text color={textColor} fontWeight="medium">
                    {formData.videoUrl ? 'Current video loaded' : 'Click to upload video'}
                  </Text>
                  <Text fontSize="sm" color={labelColor}>
                    MP4, MOV, AVI up to 32GB
                  </Text>
                </VStack>
              )}
            </Box>
            <Input
              id="video-upload"
              type="file"
              accept="video/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleVideoSelect(file);
                }
              }}
              display="none"
            />
          </FormControl>
        </VStack>
      </VStack>
    </Box>
  );
};

export default EditShorts;
